/* eslint-disable max-len */
import { makeStyles } from '@mui/styles';
import Container from 'components/Container/Container';
import config from 'config';
import { memo, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import BgSvg from 'components/Img/aircraft-min.jpg';
import ContactForm from 'components/Forms/Contact';
import snackbarHelper from 'utils/snackbarHelper';

import { Typography, Grid } from '@mui/material';
import { useNotifier } from 'redux/modules/notifier';
import { useTranslation } from '../../hooks/useTranslation';

const useStyles = makeStyles(
  (theme) => ({
    '@keyframes peaceicon': {
      '0%': {
        animationTimingFunction: 'ease-in, ease-in-out',
        transform: 'matrix(1,0,0,1,3,8)',
      },
      '50%': {
        animationTimingFunction: 'ease-in, ease-in-out',
        transform: 'matrix(1,0,0,1,3,17)',
      },
      '100%': {
        animationTimingFunction: 'ease-in, ease-in-out',
        transform: 'matrix(1,0,0,1,3,8)',
      },
    },
    bg: {
      display: 'flex',
      color: '#fff',
      backgroundImage: `url(${BgSvg})`,
      minHeight: 800,
      width: 'calc(100% + 2px)',
      backgroundSize: 'cover',
      backgroundPosition: 'center center',
    },
    container100: {
      height: '100%',
    },
    containerFeatures: {
      zIndex: 1,
      position: 'relative',
      top: -100,
      // backgroundColor: '#e1eaf8',
      '&:after': {
        height: 72,
        width: '100%',
        position: 'absolute',
        zIndex: -1,
        bottom: 0,
        content: '" "',
        display: 'block',
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' preserveAspectRatio='none' viewBox='0 0 100 100'><polygon fill='#fff' points='0,40 0,100 100,100 0,0' /></svg>\")",
        backgroundRepeat: 'no-repeat',
      },
    },
    paperFeatures: {
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'spacing' does not exist on type 'Default... Remove this comment to see the full error message
      padding: theme.spacing(10),
    },
    typoWithIcon: {
      display: 'inline-flex',
    },
    iconsWithText: {
      fontSize: 28,
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'spacing' does not exist on type 'Default... Remove this comment to see the full error message
      marginRight: theme.spacing(3),
    },
    containerSecond: {
      position: 'relative',
      bottom: -120,
    },
    containerContentSecond: {
      textAlign: 'center',
    },
    peaceicon: {
      '& g > g > g > g:nth-child(2)': {
        animationName: '$peaceicon',
        animationDuration: '3s',
        animationIterationCount: 'infinite',
      },
    },
    paper: {
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'spacing' does not exist on type 'Default... Remove this comment to see the full error message
      padding: theme.spacing(5),
      height: 400,
    },
    containerOver: {
      position: 'relative',
      top: -170,
    },
  }),
  { name: 'BringerAboutUs' }
);

const Home = memo(
  () => {
    const classes = useStyles();
    const { dispatch: dispatchNotifier } = useNotifier();

    const { t } = useTranslation();

    const GOOGLE_ADS_ID = 'AW-935380935';
    const CONVERSION_LABEL = 'WKL0CNKnrIcCEMePg74D';

    // @ts-expect-error
    useEffect(() => {
      // @ts-expect-error
      if (typeof window.gtag === 'function') {
        console.log('gtag function found, sending conversion event...');
        // @ts-expect-error
        window.gtag('event', 'conversion', { send_to: `${GOOGLE_ADS_ID}/${CONVERSION_LABEL}` });
      } else {
        console.warn('gtag function not found immediately. Retrying in 500ms...');
        const retryGtag = setTimeout(() => {
          // @ts-expect-error
          if (typeof window.gtag === 'function') {
            console.log('gtag function found on retry, sending conversion event...');
            // @ts-expect-error
            window.gtag('event', 'conversion', { send_to: `${GOOGLE_ADS_ID}/${CONVERSION_LABEL}` });
          } else {
            console.error(
              'gtag function still not found after retry. Conversion event might be missed.'
            );
          }
        }, 500);

        return () => clearTimeout(retryGtag);
      }
    }, []);

    const onSuccess = () => {
      snackbarHelper({
        dispatch: dispatchNotifier,
        message: t('public.contact.success'),
      });
    };

    const title = 'Bringer Parcel Xpress';
    // eslint-disable-next-line
    const description =
      'Ship anywhere in the world with a click. Our online tools let you ship with confidence without downloading software';

    const ldjson = {
      '@context': 'http://schema.org',
      '@type': 'WebSite',
      url: config.domain,
      name: config.app.title,
      alternateName: 'Bringer',
      image: `${config.domain}/bpx-logo-white.png`,
      sameAs: [
        'https://www.facebook.com/Bringer',
        'https://instagram.com/Bringer',
        'https://www.linkedin.com/company/Bringer/',
        'https://twitter.com/@Bringer',
      ],
    };

    const ldjsonOrg = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: config.app.title,
      legalName: 'Bringer CORP',
      email: `mailto:contact@${config.host}`,
      url: config.domain,
      logo: `${config.domain}/bpx-logo-white.png`,
      sameAs: [
        'https://www.facebook.com/Bringer',
        'https://instagram.com/Bringer',
        'https://www.linkedin.com/company/Bringer/',
        'https://twitter.com/@Bringer',
      ],
    };

    return (
      <>
        <Helmet>
          <title>{title}</title>
          <link rel="canonical" href={`${config.domain}/`} />
          <meta name="description" content={description} />
          <meta property="og:title" content={title} />
          <meta property="twitter:title" content={title} />
          <meta name="og:description" content={description} />
          <meta name="twitter:description" content={description} />
          <meta property="og:type" content="website" />
          <meta name="robots" content="index, follow" />
          <script type="application/ld+json">{JSON.stringify(ldjson)}</script>
          <script type="application/ld+json">{JSON.stringify(ldjsonOrg)}</script>
        </Helmet>
        <div className={classes.bg}>
          <Container>
            <Grid container alignItems="center" className={classes.container100}>
              <Grid item xs={12} sm={6}>
                <Typography variant="h3" component="h1" color="inherit" gutterBottom>
                  {t('public.contact')}
                </Typography>
                <Typography variant="subtitle1">{t('public.contact.subtitle')}</Typography>
              </Grid>
            </Grid>
          </Container>
        </div>
        <div className={classes.containerFeatures}>
          <Container className={classes.containerOver}>
            <Grid container className={classes.containerSecond}>
              <Grid item xs={12}>
                <Grid container spacing={2} justifyContent="center">
                  <Grid item xs={12} sm={8} className={classes.containerContentSecond}>
                    <ContactForm onSuccess={onSuccess} />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Container>
        </div>
        <br />
        <br />
      </>
    );
  },
  () => true
);

export default Home;
